import { LeftArrow, RightArrow } from '@/svg';
import React from 'react';

interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  showSizeChanger?: boolean;
  onSizeChange?: (size: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  if (totalPages <= 1) return null;

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 3;

    if (totalPages <= maxPageNumbersToShow + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      const startPage = Math.max(currentPage - 1, 2);
      const endPage = Math.min(currentPage + 1, totalPages - 1);

      if (startPage > 2) {
        pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((page, index) => (
      <span
        key={index}
        onClick={() => typeof page === 'number' && handlePageChange(page)}
        style={{
          border: page === currentPage ? '1px solid black' : '#f9f9f9',
        }}
        className="h-[25px] w-[40px] rounded-full cursor-pointer select-none flex justify-center items-center text-sm"
      >
        {page}
      </span>
    ));
  };

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center">
        <span className="text-sm text-gray-600">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{' '}
          entries
        </span>
      </div>

      <div className="flex gap-1">
        <div aria-disabled={currentPage === 1}>
          <LeftArrow
            className={'size-5 cursor-pointer'}
            onClick={() => handlePageChange(currentPage - 1)}
          />
        </div>

        <div className="flex mx-4">{renderPageNumbers()}</div>

        <div aria-disabled={currentPage === totalPages}>
          <RightArrow
            className="size-5 cursor-pointer"
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </div>
      </div>
    </div>
  );
};

export default Pagination;
